import type { AgentMessage, ThinkingLevel } from "@earendil-works/pi-agent-core";
import type { Message, TextContent } from "@earendil-works/pi-ai";
import type { ThemeColor } from "@earendil-works/pi-coding-agent";
import { AgentStatus } from "@feature-forge/core";

import type { AgentEntryBase, AgentViewerEntry, AgentViewerEntryStatus } from "../types";

/**
 * Display helpers for agent viewer rendering.
 *
 * Provides static utilities for extracting message text, resolving status
 * icons, serializing tool arguments, and safely navigating nested objects.
 * All methods are pure — no instance state, no side effects.
 */
export class AgentDisplayHelpers {
  /**
   * Format a human-readable elapsed time string.
   *
   * When `finishedAt` is provided (completed/errored agents) the duration
   * is computed from `createdAt` to `finishedAt` — stable across redraws.
   * For running agents (no `finishedAt`) the duration is live:
   * `Date.now() - createdAt`.
   */
  static formatElapsed(createdAt: Date, finishedAt?: Date): string {
    const end = finishedAt ?? new Date();
    const ms = end.getTime() - createdAt.getTime();
    const seconds = Math.floor(ms / 1000);
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ${seconds % 60}s`;
    const hours = Math.floor(minutes / 60);
    return `${hours}h ${minutes % 60}m ${seconds % 60}s`;
  }

  /**
   * Extract text content from an {@link AgentMessage}.
   *
   * Only roles with text-bearing content fields (user, toolResult, assistant)
   * produce output. Other roles return an empty string.
   */
  static extractMessageText(message: AgentMessage): string {
    if ("content" in message) {
      return AgentDisplayHelpers.extractContentText(message.content);
    }
    return "";
  }

  /**
   * Extracts text content from various content formats used in messages and tool results.
   */
  static extractContentText(content: Message["content"]): string {
    if (!content) return "";
    if (typeof content === "string") return content;
    if (Array.isArray(content)) {
      const text = content
        .filter((part) => AgentDisplayHelpers.isTextPart(part))
        .map((part) => part.text)
        .join("\n");
      if (text) return text;
    }
    return "";
  }

  /**
   * Type guard to verify if a part is a text block.
   */
  static isTextPart(part: Message["content"][number]): part is TextContent {
    return (
      typeof part === "object" &&
      part !== null &&
      "type" in part &&
      part.type === "text" &&
      "text" in part &&
      typeof part.text === "string"
    );
  }

  /**
   * Map an {@link AgentStatus} enum value to a viewer entry status.
   *
   * Exhaustive switch — every enum member maps to exactly one entry status.
   */
  static mapAgentStatus(status: AgentStatus): AgentViewerEntryStatus {
    switch (status) {
      case AgentStatus.Spawned:
        return "started";
      case AgentStatus.Running:
        return "running";
      case AgentStatus.Completed:
        return "done";
      case AgentStatus.Failed:
        return "error";
      case AgentStatus.Cancelled:
        return "cancelled";
    }
  }

  /**
   * Build an {@link AgentViewerEntry} for the requested status.
   *
   * Variant-specific defaults:
   * - `"done"` → `passed` spread only when defined (undefined renders
   *   "completed", never "failed"), `summary: params.summary ?? ""`
   * - `"error"` → `errorMessage: params.summary ?? "Agent failed"`
   * - `"started"` / `"running"` / `"cancelled"` → no `passed` field
   */
  static buildEntry(params: {
    id: string;
    status: AgentViewerEntryStatus;
    passed?: boolean;
    summary?: string;
    role?: string;
    model?: string;
    thinkingLevel?: ThinkingLevel;
    createdAt?: Date;
    finishedAt?: Date;
  }): AgentViewerEntry {
    const base: AgentEntryBase = {
      id: params.id,
      role: params.role,
      model: params.model,
      thinkingLevel: params.thinkingLevel,
      createdAt: params.createdAt ?? new Date(),
      // Spread finishedAt only when defined so a merge with an existing
      // entry never clobbers a previously stamped terminal timestamp.
      ...(params.finishedAt !== undefined ? { finishedAt: params.finishedAt } : {}),
      summary: params.summary,
    };
    switch (params.status) {
      case "started":
      case "running":
        return { ...base, status: params.status };
      case "done":
        return {
          ...base,
          status: "done",
          // Spread passed only when defined: agent-done events without a
          // parsed result keep `passed` undefined so the entry renders
          // "completed" (green), matching HEAD semantics.
          ...(params.passed !== undefined ? { passed: params.passed } : {}),
          summary: params.summary ?? "",
        };
      case "error":
        return {
          ...base,
          status: "error",
          errorMessage: params.summary ?? "Agent failed",
        };
      case "cancelled":
        return { ...base, status: "cancelled" };
    }
  }

  static getStatusLabel(
    status: string | undefined,
    passed?: boolean,
  ): { label: string; color: ThemeColor } {
    switch (status) {
      case "started":
      case "running":
        return { label: "running", color: "accent" };
      case "cancelled":
        return { label: "cancelled", color: "muted" };
      case "done":
        return passed === false
          ? { label: "failed", color: "error" }
          : { label: "completed", color: "success" };
      case "error":
        return { label: "error", color: "error" };
      default:
        return { label: status ?? "unknown", color: "muted" };
    }
  }

  /**
   * Extract the `passed` flag from the discriminated union.
   *
   * Only {@link CompletedAgentEntry} carries this field. Returns
   * `undefined` for "started", "running", "cancelled", and "error"
   * statuses where the concept does not apply.
   */
  static getEntryPassed(entry: AgentViewerEntry): boolean | undefined {
    return entry.status === "done" ? entry.passed : undefined;
  }

  /**
   * Resolve a status string to an icon character and theme colour tuple.
   *
   * - `"done"` + `passed !== false` → `{ char: "✓", color: "success" }`
   * - `"done"` + `passed === false` → `{ char: "✗", color: "error" }`
   * - `"running"` → `{ char: "⟳", color: "accent" }`
   * - `"started"` → `{ char: "⟳", color: "accent" }`
   * - `"error"` → `{ char: "✗", color: "error" }`
   * - `"cancelled"` → `{ char: "○", color: "muted" }`
   * - anything else → `{ char: "○", color: "muted" }`
   */
  static getStatusIcon(
    status: string | undefined,
    passed?: boolean,
  ): { char: string; color: ThemeColor } {
    switch (status) {
      case "done":
        return passed === false ? { char: "✗", color: "error" } : { char: "✓", color: "success" };
      case "started":
      case "running":
        return { char: "⟳", color: "accent" };
      case "error":
        return { char: "✗", color: "error" };
      case "cancelled":
        return { char: "○", color: "muted" };
      default:
        return { char: "○", color: "muted" };
    }
  }

  /**
   * Serialize tool call arguments to a stable string representation.
   *
   * Attempts JSON serialization first; falls back to {@code String()} for
   * non-serializable values (BigInt, circular references, etc.).
   */
  static serializeToolArgs(args: unknown): string {
    if (typeof args === "string") return args;
    try {
      const serialized = JSON.stringify(args, null, 2);
      if (serialized !== undefined) return serialized;
    } catch {
      // Fall through to string coercion.
    }
    return String(args);
  }

  /**
   * Extract human-readable text from a tool execution result or update payload.
   *
   * The runtime type is {@code AgentToolResult} which carries {@code content}
   * as an array of {@code { type: "text", text: string }} blocks.
   * This method extracts text from those blocks, falling back to
   * JSON serialization for non-text content and returning empty string
   * for null/undefined.
   */
  static serializeToolResultText(result: unknown): string {
    if (result === null || result === undefined) {
      return "";
    }
    if (typeof result === "string") {
      return result;
    }
    // Handle AgentToolResult shape: { content: [{ type: "text", text: "..." }], details: ... }
    const obj = result as Record<string, unknown>;
    if (typeof obj === "object" && "content" in obj && Array.isArray(obj.content)) {
      const parts: string[] = [];
      for (const block of obj.content) {
        if (
          typeof block === "object" &&
          block !== null &&
          "type" in block &&
          (block as Record<string, unknown>).type === "text" &&
          "text" in block &&
          typeof (block as { text: unknown }).text === "string"
        ) {
          parts.push((block as { text: string }).text);
        }
      }
      if (parts.length > 0) {
        return parts.join("\n");
      }
    }
    // Fall back to JSON serialization.
    try {
      const serialized = JSON.stringify(result, null, 2);
      if (serialized !== undefined) return serialized;
    } catch {
      // Fall through to string coercion.
    }
    // eslint-disable-next-line @typescript-eslint/no-base-to-string -- typed `unknown`, fallback only; String() is the safe last resort
    return String(result);
  }
}
